'use client';

import './honor-card.scss';
import { useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '@sola/ark-core/atoms/image/image.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { coverCategoryTitleColor, getBackgroundSrc } from '../../organisms-cms/honor-card-builder/honor-card-builder.data.ts';

// Desktop and mobile use different view counts (3 vs 4), so we need JS to
// detect breakpoint changes and reset the view index to avoid showing the
// wrong panel. CSS alone cannot reset React state.
const subscribeResize = (callback: () => void) => {
  globalThis.addEventListener('resize', callback);
  return () => globalThis.removeEventListener('resize', callback);
};
const getIsMobile = () => globalThis.innerWidth <= 768;

/**
 * Imagine a folded Christmas card. On desktop we show the front of the card
 * and then when the user clicks to open it, they see two inside panels for the card,
 * and when they click again they see the back of the card.
 * On mobile since the screen is narrower we show the inside panels one at a time,
 * instead simulating how an actual card looks when you hold it in your hands.
 */
const desktopViews = ['front', 'inside', 'back'] as const;
const mobileViews = ['front', 'inside-left', 'inside-right', 'back'] as const;
type HonorCardVariant = 'interactive' | 'preview';
export interface HonorCardProps {
  variant?: HonorCardVariant;
  coverCategory: string;
  coverNumber: number;
  giftLabel?: string;
  giftMessage?: string;
  personalNote?: string;
  label?: string;
}
export const HonorCard: React.FC<HonorCardProps> = ({
  variant = 'interactive',
  coverCategory,
  coverNumber,
  giftLabel,
  giftMessage,
  personalNote,
  label
}) => {
  const {
    t
  } = useTranslation(['honor-card']);
  const [view, setView] = useState(0);
  const isMobile = useSyncExternalStore(subscribeResize, getIsMobile);

  // Reset view to front when the breakpoint changes
  const prevMobileRef = useRef(isMobile);
  if (prevMobileRef.current !== isMobile) {
    prevMobileRef.current = isMobile;
    setView(0);
  }
  const views = isMobile ? mobileViews : desktopViews;
  const totalViews = views.length;
  const handleClick = () => {
    trackClick('Honor Card - Flip Card Pages');
    setView(prev => (prev + 1) % totalViews);
  };
  const currentView = views[view];
  const isWide = currentView === 'inside';
  const bgSrc = getBackgroundSrc(coverCategory, coverNumber);
  const titleColor = coverCategoryTitleColor[coverCategory] ?? '#333';
  const labelText = label ?? (variant === 'preview' ? t('honor-card:preview') : t('honor-card:interactive'));
  return <div className={cn('HonorCard', `HonorCard--${variant}`, 'flex flex-col items-center gap-snug-lg p-comfy-lg', variant === 'interactive' ? 'justify-center' : 'w-full')} onClick={handleClick} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleClick();
    }
  }} role="button" tabIndex={0}>
      <div className="label-moderate label-sm">{labelText}</div>

      <div className="flex gap-snug-lg mb-snug-lg">
        {Array.from({
        length: totalViews
      }, (_, i) => <span key={i} className={cn('HonorCard__dot', view === i && 'HonorCard__dot--active')} />)}
      </div>

      <div className={cn('HonorCard__card relative overflow-hidden', isWide && 'HonorCard__card--wide')}>
        {/* Front — bottom-right quadrant */}
        {currentView === 'front' && <div className="HonorCard__view absolute overflow-hidden HonorCard__view--front">
            <Image src={bgSrc} alt={t('honor-card:cardFront')} className="HonorCard__bg absolute" width={1700} height={2200} sizes="(max-width: 600px) 320px, 1000px" />
          </div>}

        {/* Inside (desktop) — top half rotated */}
        {currentView === 'inside' && <div className="HonorCard__view absolute overflow-hidden HonorCard__view--inside">
            <Image src={bgSrc} alt={t('honor-card:cardInside')} className="HonorCard__bg absolute" width={1700} height={2200} sizes="(max-width: 600px) 640px, 1600px" />
            <div className="HonorCard__overlay">
              <div className="HonorCard__overlay-left">
                {giftLabel && <>
                    <p className="HonorCard__gift-title" style={{
                color: titleColor
              }}>
                      {giftLabel}
                    </p>
                    <p className="HonorCard__gift-message">{giftMessage}</p>
                  </>}
              </div>
              <div className="HonorCard__overlay-right">
                {personalNote && <p className="HonorCard__personal-note" style={{
              whiteSpace: 'pre-line'
            }}>
                    {personalNote}
                  </p>}
              </div>
            </div>
          </div>}

        {/* Inside-left (mobile) — top-left quadrant */}
        {currentView === 'inside-left' && <div className="HonorCard__view absolute overflow-hidden HonorCard__view--inside-left">
            <Image src={bgSrc} alt={t('honor-card:cardInsideLeft')} className="HonorCard__bg absolute" width={1700} height={2200} sizes="320px" />
            <div className="HonorCard__overlay-single absolute">
              {giftLabel && <>
                  <p className="HonorCard__gift-title" style={{
              color: titleColor
            }}>
                    {giftLabel}
                  </p>
                  <p className="HonorCard__gift-message">{giftMessage}</p>
                </>}
            </div>
          </div>}

        {/* Inside-right (mobile) — top-right quadrant */}
        {currentView === 'inside-right' && <div className="HonorCard__view absolute overflow-hidden HonorCard__view--inside-right">
            <Image src={bgSrc} alt={t('honor-card:cardInsideRight')} className="HonorCard__bg absolute" width={1700} height={2200} sizes="320px" />
            <div className="HonorCard__overlay-single absolute">
              {personalNote && <p className="HonorCard__personal-note">{personalNote}</p>}
            </div>
          </div>}

        {/* Back — bottom-left quadrant */}
        {currentView === 'back' && <div className="HonorCard__view absolute overflow-hidden HonorCard__view--back">
            <Image src={bgSrc} alt={t('honor-card:cardBack')} className="HonorCard__bg absolute" width={1700} height={2200} sizes="(max-width: 600px) 320px, 1000px" />
          </div>}
      </div>
    </div>;
};